import { ChangeDetectorRef, Component, effect, Inject, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FileUploadModule } from '@sbd-doug-frontend/shared/lib/ui/file-upload/file-upload.module';
import { FileUploadConfig } from '@sbd-doug-frontend/shared/lib/ui/file-upload/file-upload.component';
import { FileUploadService } from '@sbd-doug-frontend/shared/lib/ui/file-upload/file-upload.service';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { FileCabinetDestination, FilePurpose } from '@sbd-doug-frontend/shared/lib/types/file.types';

/**
 * Shared file uploader modal.
 *
 * Backward-compatible behaviors by context:
 * - cipxtask: select files and immediately return them to the caller (no upload here)
 * - default / cipquotereq: auto-upload as soon as files are selected
 *
 * Opt-in File Cabinet behaviors:
 * - `requiresDestination`: ask "Where should this be saved?" and derive file_purpose_id from folder
 * - `requiresFilePurpose`: legacy purpose selector (still supported if a caller opts in)
 * - pre-selected `selectedFilePurposeId`: purpose already known from current folder
 * - always send `file_purpose_id` when a purpose id is selected
 */
@Component({
  selector: 'sbd-doug-frontend-file-uploader-modal',
  standalone: true,
  imports: [CommonModule, FileUploadModule, MatFormFieldModule, MatSelectModule, MatButtonModule, FormsModule],
  templateUrl: './file-uploader-modal.component.html',
})
export class FileUploaderModalComponent implements OnInit {
  _changeDetector = inject(ChangeDetectorRef);
  uploadConfig: FileUploadConfig = {};
  contextId: string = '';
  context: string = '';
  isUploading: boolean = false;
  queuedFiles: File[] = [];
  uploadUrl: string = '';
  httpParamsName: string = '';
  title: string = 'Attach File(s)';

  /** Opt-in: only populated when caller (e.g. File Cabinet) provides purposes. */
  filePurposes: FilePurpose[] = [];
  /** Stable grouped list for mat-optgroup (rebuilt when filePurposes changes). */
  filePurposeGroups: { label: string; purposes: FilePurpose[] }[] = [];
  selectedFilePurposeId: number | undefined;
  /** Explicit opt-in flag from caller; also true when filePurposes are provided without destination mode. */
  private _requiresFilePurposeFlag = false;
  loadingPurposes = false;

  /** File Cabinet Home: choose destination folder instead of purpose. */
  requiresDestination = false;
  destinations: FileCabinetDestination[] = [];
  selectedDestinationCode: string | undefined;
  /** When purpose is already known and files are queued, start upload immediately. */
  autoStartUpload = false;

  private _fileUploadService = inject(FileUploadService);
  private _cathyService = inject(CathyService);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<FileUploaderModalComponent>,
  ) {
    effect(() => {
      this.isUploading = this._fileUploadService.isUploading();
    });
  }

  /**
   * True only when the caller opted into the legacy purpose selector.
   * Destination mode supersedes purpose UI.
   */
  get requiresFilePurpose(): boolean {
    if (this.requiresDestination) {
      return false;
    }
    return this._requiresFilePurposeFlag || (Array.isArray(this.filePurposes) && this.filePurposes.length > 0 && !this.selectedFilePurposeId);
  }

  /** True when the dialog needs a manual confirm step (destination or purpose not yet ready). */
  get needsManualConfirm(): boolean {
    if (this.requiresDestination) {
      return true;
    }
    if (this.requiresFilePurpose) {
      return true;
    }
    // Purpose pre-selected from folder but user still picking files in the dialog
    return false;
  }

  get canUpload(): boolean {
    if (!this.queuedFiles?.length || this.isUploading || this.loadingPurposes) {
      return false;
    }
    if (this.requiresDestination && !this.selectedDestinationCode) {
      return false;
    }
    if ((this.requiresDestination || this.requiresFilePurpose || this.selectedFilePurposeId != null) && !this.selectedFilePurposeId) {
      return false;
    }
    return true;
  }

  /**
   * Rebuild category groups whenever filePurposes changes.
   * Kept as a stable array (not a getter) so mat-select/mat-optgroup is not
   * recreated on every change-detection cycle — that was breaking the dialog.
   */
  private rebuildFilePurposeGroups(): void {
    const groups = new Map<string, FilePurpose[]>();

    for (const purpose of this.filePurposes || []) {
      if (!purpose || purpose.file_purpose_id == null) {
        continue;
      }

      const label = String(
        purpose.file_purpose_category_label ||
          purpose.file_purpose_category_code ||
          'Other',
      ).trim() || 'Other';

      if (!groups.has(label)) {
        groups.set(label, []);
      }
      groups.get(label)!.push(purpose);
    }

    this.filePurposeGroups = Array.from(groups.entries())
      .map(([label, purposes]) => ({
        label,
        purposes: [...purposes].sort((a, b) =>
          String(a.file_purpose_label || '').localeCompare(String(b.file_purpose_label || '')),
        ),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private setFilePurposes(purposes: FilePurpose[] | null | undefined): void {
    this.filePurposes = Array.isArray(purposes) ? purposes.filter((p) => !!p && p.file_purpose_id != null) : [];
    this.rebuildFilePurposeGroups();
  }

  private resolvePurposeFromDestination(code: string | undefined): FilePurpose | undefined {
    if (!code || !this.filePurposes?.length) {
      return undefined;
    }

    const normalized = String(code).trim().toLowerCase();
    const destination = this.destinations.find((d) => String(d.code).toLowerCase() === normalized);
    const label = String(destination?.label || '').trim().toLowerCase();

    const byCategory = this.filePurposes.filter((p) => {
      const catCode = String(p.file_purpose_category_code || '').trim().toLowerCase();
      const catLabel = String(p.file_purpose_category_label || '').trim().toLowerCase();
      return catCode === normalized || (!!label && catLabel === label);
    });

    if (byCategory.length === 1) {
      return byCategory[0];
    }
    if (byCategory.length > 1) {
      return (
        byCategory.find((p) => String(p.file_purpose_code || '').toLowerCase() === normalized) ||
        byCategory.find((p) => String(p.file_purpose_label || '').toLowerCase() === label) ||
        byCategory[0]
      );
    }

    return (
      this.filePurposes.find((p) => String(p.file_purpose_code || '').toLowerCase() === normalized) ||
      this.filePurposes.find((p) => String(p.file_purpose_code || '').toLowerCase() === 'other')
    );
  }

  private applyDestinationSelection(code: string | undefined, attemptUpload = false): void {
    this.selectedDestinationCode = code || undefined;
    const purpose = this.resolvePurposeFromDestination(this.selectedDestinationCode);
    this.selectedFilePurposeId = purpose?.file_purpose_id != null ? Number(purpose.file_purpose_id) : undefined;

    if (attemptUpload && this.canUpload) {
      this.startUpload();
    }

    this._changeDetector.markForCheck();
  }

  private shouldHoldForSelection(): boolean {
    // Wait for destination / purpose before uploading
    if (this.requiresDestination && !this.selectedFilePurposeId) {
      return true;
    }
    if (this.requiresFilePurpose && !this.selectedFilePurposeId) {
      return true;
    }
    return false;
  }

  ngOnInit() {
    this.uploadConfig = this.data?.uploadConfig ?? {};

    // Optional overrides used by File Cabinet and any future custom endpoints
    if (this.data?.endpoint) {
      this.uploadUrl = this.data.endpoint;
    }
    if (this.data?.httpParamsName) {
      this.httpParamsName = this.data.httpParamsName;
    }
    if (this.data?.title) {
      this.title = this.data.title;
    }

    if (this.data?.context) {
      this.context = this.data.context;
      switch (this.data.context) {
        case 'cipquotereq':
          this.contextId = this.data.id;
          if (!this.httpParamsName) this.httpParamsName = 'quote_request_files';
          if (!this.data?.title) this.title = 'Attach Quote Request File(s)';
          break;

        case 'cipxtask':
          if (!this.httpParamsName) this.httpParamsName = 'task_files';
          if (!this.data?.title) this.title = 'Attach Task File(s)';
          break;

        case 'cipxaccount':
          if (!this.httpParamsName) this.httpParamsName = 'files';
          if (!this.data?.title) this.title = 'Attach Account File(s)';
          break;

        default:
          break;
      }
    }

    // Destination mode (File Cabinet Home)
    this.requiresDestination = !!this.data?.requiresDestination;
    this.destinations = Array.isArray(this.data?.destinations) ? this.data.destinations : [];
    this.autoStartUpload = !!this.data?.autoStartUpload;

    // Opt-in purpose selection — legacy / fallback
    this._requiresFilePurposeFlag = !!this.data?.requiresFilePurpose;
    if (Array.isArray(this.data?.filePurposes)) {
      this.setFilePurposes(this.data.filePurposes);
    } else {
      this.rebuildFilePurposeGroups();
    }
    if (this.data?.selectedFilePurposeId != null) {
      this.selectedFilePurposeId = Number(this.data.selectedFilePurposeId);
    }
    if (this.data?.selectedDestinationCode) {
      this.selectedDestinationCode = String(this.data.selectedDestinationCode);
    }

    // If folder/destination is known but purpose wasn't pre-resolved, derive it now
    if (this.selectedDestinationCode && !this.selectedFilePurposeId && this.filePurposes.length > 0) {
      this.applyDestinationSelection(this.selectedDestinationCode, false);
    }

    // If destination/purpose resolution needs purposes and list is empty, load them
    const needsPurposes =
      this.filePurposes.length === 0 &&
      (this.requiresDestination ||
        this._requiresFilePurposeFlag ||
        !!this.selectedDestinationCode ||
        this.autoStartUpload ||
        this.selectedFilePurposeId != null);

    if (needsPurposes) {
      this.loadingPurposes = true;
      this._fileUploadService.getFilePurposes().subscribe({
        next: (purposes) => {
          const filtered = (purposes || []).filter((p) => {
            if (!p || p.file_purpose_id == null) {
              return false;
            }
            const isSystem = p.is_system === true || p.is_system === 1 || p.is_system === '1';
            const isOther = p.file_purpose_code === 'other';
            const isActive =
              p.is_active === undefined ||
              p.is_active === true ||
              p.is_active === 1 ||
              p.is_active === '1';
            return (isSystem || isOther) && isActive;
          });
          this.setFilePurposes(filtered);

          // Re-resolve folder/destination → purpose after lookups load
          if (this.selectedDestinationCode && !this.selectedFilePurposeId) {
            this.applyDestinationSelection(this.selectedDestinationCode, false);
          }

          this.loadingPurposes = false;
          this._changeDetector.markForCheck();

          if ((this.autoStartUpload || (!this.requiresDestination && !!this.selectedFilePurposeId)) && this.canUpload) {
            this.startUpload();
          }
        },
        error: () => {
          this.loadingPurposes = false;
          this._cathyService.error('Failed to load file destinations.');
          this._changeDetector.markForCheck();
        },
      });
    }

    // Pre-queued files (drag/drop or click-to-browse from File Cabinet)
    if (Array.isArray(this.data?.queuedFiles) && this.data.queuedFiles.length > 0) {
      this.queuedFiles = this.data.queuedFiles;
      this.uploadConfig = {
        ...this.uploadConfig,
        caption: `(${this.queuedFiles.length}) File(s) selected`,
      };

      setTimeout(() => {
        if (!this.queuedFiles.length) {
          return;
        }
        // Destination required at Home — wait for selection
        if (this.shouldHoldForSelection()) {
          return;
        }
        // Auto-start when purpose already known (in-folder drop) or non-cabinet flows
        if (this.autoStartUpload || !this.needsManualConfirm) {
          this.onFileUploadChanged(this.queuedFiles);
        }
      }, 0);
    }
  }

  onDestinationChanged(): void {
    this.applyDestinationSelection(this.selectedDestinationCode, this.queuedFiles.length > 0);
  }

  onPurposeChanged(): void {
    // Keep selection; files stay queued if already chosen.
    if (this.queuedFiles.length > 0 && this.selectedFilePurposeId && this.requiresFilePurpose) {
      // User still clicks Upload for purpose mode to avoid surprise uploads
    }
    this._changeDetector.markForCheck();
  }

  onFileUploadChanged(files: File[]) {
    this.queuedFiles = files || [];

    // Task attachments: return files to caller, do not upload here
    if (this.context === 'cipxtask' || this.data?.context === 'cipxtask') {
      this.dialogRef.close({
        data: { queuedFiles: this.queuedFiles },
      });
      return;
    }

    // Wait until destination / purpose is chosen
    if (this.shouldHoldForSelection()) {
      return;
    }

    // File Cabinet with purpose already resolved from folder: upload as soon as files are chosen
    if (this.selectedFilePurposeId && !this.requiresDestination && !this.requiresFilePurpose) {
      this.startUpload();
      return;
    }

    // Destination mode: upload once both folder and files are ready
    if (this.requiresDestination) {
      if (this.selectedFilePurposeId && this.queuedFiles.length > 0) {
        this.startUpload();
      }
      return;
    }

    // Legacy purpose mode: wait for manual Upload click
    if (this.requiresFilePurpose) {
      return;
    }

    // Default / quote request: auto-upload immediately (original behavior)
    this.startUpload();
  }

  startUpload() {
    if (!this.queuedFiles || this.queuedFiles.length === 0) {
      return;
    }

    if (this.requiresDestination && !this.selectedDestinationCode) {
      this._cathyService.error('Please choose where this file should be saved.');
      return;
    }

    // Derive purpose from destination if needed
    if (this.requiresDestination && !this.selectedFilePurposeId && this.selectedDestinationCode) {
      this.applyDestinationSelection(this.selectedDestinationCode, false);
    }

    if ((this.requiresDestination || this.requiresFilePurpose) && !this.selectedFilePurposeId) {
      this._cathyService.error(
        this.requiresDestination
          ? 'Could not determine the file type for the selected folder.'
          : 'Please select a file purpose before uploading.',
      );
      return;
    }

    // Attach purpose whenever we have one (cabinet folder or selector)
    const bodyFields: { [key: string]: string | number } = {};
    let params: HttpParams | null = null;

    if (this.selectedFilePurposeId) {
      bodyFields['file_purpose_id'] = this.selectedFilePurposeId;
      params = new HttpParams().set('file_purpose_id', this.selectedFilePurposeId.toString());
    }

    this._fileUploadService
      .uploadFiles(
        this.uploadUrl,
        params,
        this.httpParamsName,
        this.queuedFiles,
        bodyFields,
      )
      .subscribe((ev) => {
        if (!this._fileUploadService.isUploading() && ev == 100) {
          const response = this._fileUploadService.uploadResponse();
          if (response && response.success !== false) {
            this.dialogRef.close(true);
            this._cathyService.success(response.message || 'Files uploaded successfully');
          }
        }
      });
  }
}
